import React, {useEffect, useRef, useState} from "react";
import { useFormik } from 'formik';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';
import { MainLayout } from "../../components/MainLayout";
import { Toast } from 'primereact/toast';
import Loading from "../../components/shared/Loading";
import { loginUserGenerate, loginUserVerify } from "../../api/methods";
import { USERNAME, ROLE, PERMISSION } from '../../constants/common';
import { PASSWORD_REGEX } from '../../constants/validation';
import { useUser } from '../../context/user';
import OTPModal from "../ForgotPassword/Component/OTPModal";
import "./Login.styles.scss";
import { useNavigate, useLocation } from "react-router-dom";


const Login = (props) => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const location = useLocation();
    const { dispatchUser } = useUser();
    const [otpModalOpen, setOtpModalOpen] = useState(false);
    const [loginData, setLoginData] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otpTimerSeconds, setOtpTimerSeconds] = useState(0);
    const [otpError, setOtpError] = useState('');

    const goToForgotPassword = () => {
        navigate("/forgotPassword");
    }

    useEffect(() => {
        if (location.state?.showToast && location.state.toastMessage) {
            setTimeout(() => {
                toast.current?.show({
                    severity: "success",
                    summary: "Success ",
                    detail: location.state.toastMessage,
                    life: 3000
                });
            }, 100);

            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);



    const formik = useFormik({
        initialValues: {
            username: '',
            password: ''
        },
        validate: (data) => {
            let errors = {};

            if (!data.username) {
                errors.username = 'Username is required.';
            }

            if (!data.password) {
                errors.password = 'Password is required.';
            } else if (!PASSWORD_REGEX.test(data.password)) {
                errors.password = 'Password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.';
            }

            return errors;
        },
        onSubmit: (data) => {
            setLoader(true);

            loginUserGenerate(data)
                .then((response) => {
                    setLoader(false);

                    if (response.otpRequired === false) {
                        sessionStorage.setItem(USERNAME, data.username);
                        localStorage.setItem(USERNAME, data.username);
                        localStorage.setItem(ROLE, response.role);
                        localStorage.setItem(PERMISSION, response.permission);

                        dispatchUser({
                            type: 'SET_USER',
                            payload: { userName: data.username, role: response.role, permission: response.permission },
                        });

                        navigate("/dashboard");
                    } else {
                        setLoginData(data);
                        setPhoneNumber(response.otpPhoneNumber || '');
                        setOtpTimerSeconds((response.otpExpiryMinutes ?? 3) * 60);
                        setOtpModalOpen(true);
                    }
                })
                .catch((error) => {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Login Failed',
                        detail: error?.message || error?.error || 'Invalid credentials. Please check your username and password.',
                        life: 5000
                    });
                    setLoader(false);
                });
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const handleOtpVerify = async (otpCode) => {
        setLoader(true);

        try {
            const verifyData = {
                username: loginData.username,
                password: loginData.password,
                otp: otpCode,
            };

            const user = await loginUserVerify(verifyData);

            sessionStorage.setItem(USERNAME, loginData.username);
            localStorage.setItem(USERNAME, loginData.username);
            localStorage.setItem(ROLE, user.role);
            localStorage.setItem(PERMISSION, user.permission);

            dispatchUser({
                type: 'SET_USER',
                payload: { userName: loginData.username, role: user.role, permission: user.permission },
            });

            setOtpModalOpen(false);
            navigate("/dashboard");
        } catch (error) {
            setOtpError(error?.message || error?.error || 'Invalid OTP. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleOtpResend = () => {
        if (!loginData) return;
        setOtpError('');
        setOtpModalOpen(false);
        setLoader(true);
        loginUserGenerate(loginData)
            .then((response) => {
                setPhoneNumber(response.otpPhoneNumber || '');
                setOtpTimerSeconds((response.otpExpiryMinutes ?? 3) * 60);
                setOtpModalOpen(true);
            })
            .catch((error) => {
                toast.current.show({
                    severity: 'error',
                    summary: 'Resend Failed',
                    detail: error?.message || error?.error || 'Failed to resend OTP.',
                    life: 5000
                });
            })
            .finally(() => setLoader(false));
    };

    const handleOtpClose = () => {
        setOtpModalOpen(false);
        setLoginData(null);
        setPhoneNumber('');
        setOtpTimerSeconds(0);
        setOtpError('');
    };

    return (
        <MainLayout hideHeader={true}>
            <Toast ref={toast} />
            {loader && <Loading />}

            <div className="login-container">
                {/* Left Panel - Login Form */}
                <div className="login-form-panel">
                    <div className="form-header">
                        <h2>Welcome Back</h2>
                        <p>Please sign in to your account to continue</p>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="login-form">
                        <div className="field">
                            <label
                                htmlFor="username"
                                className={classNames({ 'p-error': isFormFieldValid('username') })}
                            >
                                Username
                            </label>
                            <div className="input-group">
                                <i className="pi pi-user input-icon"></i>
                                <InputText
                                    id="username"
                                    name="username"
                                    value={formik.values.username}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter your username"
                                    className={classNames({ 'p-invalid': isFormFieldValid('username') })}
                                />
                            </div>
                            {getFormErrorMessage('username')}
                        </div>

                        <div className="field">
                            <label
                                htmlFor="password"
                                className={classNames({ 'p-error': isFormFieldValid('password') })}
                            >
                                Password
                            </label>
                            <div className="input-group">
                                <i className="pi pi-lock input-icon"></i>
                                <Password
                                    id="password"
                                    name="password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    feedback={false}
                                    placeholder="Enter your password"
                                    toggleMask
                                    className={classNames({ 'p-invalid': isFormFieldValid('password') })}
                                />
                            </div>
                            {getFormErrorMessage('password')}
                        </div>

                        {/* <div className="form-options">
                            <div className="remember-me">
                                <input id="remember" type="checkbox" />
                                <label htmlFor="remember">Remember me</label>
                            </div>
                            <a href="#" className="forgot-password">
                                Forgot password?
                            </a>
                        </div> */}

                        <Button
                            type="submit"
                            label={loader ? "Signing In..." : "Sign In"}
                            icon="pi pi-sign-in"
                            iconPos="right"
                            className="login-button"
                            disabled={loader}
                        />

                        <div className="forgot-password-link">
    <span
        role="button"
        tabIndex={0}
        onClick={!loader ? goToForgotPassword : undefined}
        onKeyDown={(e) =>
            !loader && e.key === "Enter" && goToForgotPassword()
        }
        style={{
            pointerEvents: loader ? "none" : "auto",
            opacity: loader ? 0.6 : 1,
            cursor: loader ? "not-allowed" : "pointer"
        }}
    >
        Forgot your password ?
    </span>
                        </div>
                    </form>

                    <div className="form-footer">
                        <div className="divider">
                            <span>Need assistance?</span>
                        </div>
                        <div className="support-info">
                            For technical support, contact{' '}
                            <a href="https://b-tek.dev/" target="_blank" rel="noopener noreferrer">
                                B-TEK Support Team
                            </a>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Branding */}
                <div className="login-brand-panel">
                    <div className="decoration decoration-1"></div>
                    <div className="decoration decoration-2"></div>

                    <div className="brand-content">
                        <img src="/logo192.png" alt="Logo" />
                        <h1>Jofotara Tax Administration Dashboard</h1>
                        <p>
                            Modern tax administration solution that digitizes revenue collection processes, enhances taxpayer services, and provides real-time insights for informed policy decisions.
                        </p>

        <ul className="feature-list">
            <li>Tax Collection Management</li>
            <li>Taxpayer Registration & Monitoring</li>
            <li>Compliance Tracking & Enforcement</li>
            <li>Revenue Analytics & Reporting</li>
            <li>Audit Management System</li>
        </ul>
                    </div>
                </div>
            </div>

            <OTPModal
                isOpen={otpModalOpen}
                onClose={handleOtpClose}
                onVerify={handleOtpVerify}
                onResend={handleOtpResend}
                phoneNumber={phoneNumber}
                initialTimer={otpTimerSeconds}
                error={otpError}
                onErrorClear={() => setOtpError('')}
            />
        </MainLayout>
    );
};

export default React.memo(Login);
